import React, { useState } from "react";
import { Send, Loader2, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const BUSINESS_TYPES = [
  "Cleaning",
  "Trades",
  "Landscaping",
  "Retail",
  "Hospitality",
  "Other",
];

const REFERRAL_SOURCES = [
  "Google search",
  "Social media",
  "Word of mouth",
  "Other",
];

export default function AskUsSection() {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    contact_email: "",
    business_type: "",
    message: "",
    referral_source: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.business_name ||
      !form.contact_email ||
      !form.business_type ||
      !form.message ||
      !form.referral_source
    ) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/forms/ask", form);
      setSent(true);
      toast.success("Thanks — we will be in touch within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="free-consultation"
      data-testid="free-consultation-section"
      className="relative py-20 sm:py-24 border-y border-[#06B6D4]/40 bg-[#0A1628]"
    >
      {/* Cyan accent bar at top */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#06B6D4] to-transparent"
      />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] so-glow-cyan opacity-40 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 grid md:grid-cols-5 gap-10 items-start">
        <div className="md:col-span-2">
          <div className="inline-flex items-center gap-2 uppercase text-xs tracking-widest text-[#06B6D4] font-mono">
            <Sparkles size={12} />
            Get in touch
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Ready to do more with AI?
          </h2>
          <p className="mt-4 text-[#94A3B8] leading-relaxed">
            Tell us what is slowing your business down. We will come back to
            you with honest thoughts on whether AI could help — no commitment,
            no sales pitch.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="free-consultation-form"
          className="md:col-span-3 so-card rounded-2xl p-6 sm:p-8 border-[#06B6D4]/20"
        >
          {sent ? (
            <div data-testid="free-consultation-sent" className="text-center py-10">
              <div className="font-display text-2xl font-bold tracking-tight">
                Thanks — we will be in touch within one business day.
              </div>
              <p className="mt-3 text-[#94A3B8] text-sm leading-relaxed">
                We&apos;ll reply to{" "}
                <span className="text-[#06B6D4]">{form.contact_email}</span>.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">Your name</label>
                <input
                  data-testid="consult-name"
                  value={form.name}
                  onChange={onChange("name")}
                  required
                  className="so-input w-full rounded-lg px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">Business name</label>
                <input
                  data-testid="consult-business"
                  value={form.business_name}
                  onChange={onChange("business_name")}
                  required
                  className="so-input w-full rounded-lg px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">Email address</label>
                <input
                  data-testid="consult-email"
                  type="email"
                  value={form.contact_email}
                  onChange={onChange("contact_email")}
                  required
                  className="so-input w-full rounded-lg px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">What type of business are you?</label>
                <select
                  data-testid="consult-business-type"
                  value={form.business_type}
                  onChange={onChange("business_type")}
                  required
                  className="so-input w-full rounded-lg px-4 py-3 text-sm bg-[#0A1628] appearance-none"
                >
                  <option value="" disabled>Select an option…</option>
                  {BUSINESS_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">What problem are you trying to solve?</label>
                <textarea
                  data-testid="consult-message"
                  value={form.message}
                  onChange={onChange("message")}
                  rows={4}
                  required
                  className="so-input w-full rounded-lg px-4 py-3 text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">How did you hear about us?</label>
                <select
                  data-testid="consult-referral"
                  value={form.referral_source}
                  onChange={onChange("referral_source")}
                  required
                  className="so-input w-full rounded-lg px-4 py-3 text-sm bg-[#0A1628] appearance-none"
                >
                  <option value="" disabled>Select an option…</option>
                  {REFERRAL_SOURCES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="consult-submit"
                className="btn-blue w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium disabled:opacity-60"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send us a message
              </button>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
